import { useCallback, useMemo, useReducer, useState } from 'react';

import { IMPORT_ACTIONS } from '../../../domains/people-access/constants/importWizardActions.js';
import { ImportWizardContext } from './importWizardContext.js';
import { importWizardReducer, initialImportWizardState } from './importWizardReducer.js';

/**
 * Owns the wizard state (`useReducer`, no global store). It lives only while the import route
 * is mounted, so leaving the route drops the file and everything derived from it.
 */
function ImportWizardProvider({ children }) {
  const [state, dispatch] = useReducer(importWizardReducer, initialImportWizardState);
  // A file is being read. Lives here (not in the upload step) so the wizard shell can hold Next
  // while it lasts: advancing mid-parse would unmount the step and drop the result or its error.
  const [parsing, setParsing] = useState(false);

  // The reducer decides whether this is a first load or a replace, so the callback stays stable.
  const loadFile = useCallback(
    (sheet) => dispatch({ type: IMPORT_ACTIONS.FILE_LOADED, payload: sheet }),
    [],
  );
  const setStep = useCallback(
    (step) => dispatch({ type: IMPORT_ACTIONS.SET_STEP, payload: step }),
    [],
  );
  const setMapping = useCallback(
    (column, target) => dispatch({ type: IMPORT_ACTIONS.SET_MAPPING, payload: { column, target } }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: IMPORT_ACTIONS.RESET }), []);

  const value = useMemo(
    () => ({ state, dispatch, loadFile, setStep, setMapping, reset, parsing, setParsing }),
    [state, loadFile, setStep, setMapping, reset, parsing],
  );

  return <ImportWizardContext.Provider value={value}>{children}</ImportWizardContext.Provider>;
}

export default ImportWizardProvider;
